// TelcoTrack — PPD material plan (finance-comparison/material-lines) for materials Souhrn.

import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ObjectService } from '../../core/services/object.service';

export interface MaterialsPlanSapLine {
  plannedCost: number;
  plannedQuantity: number;
  unit: string | null;
}

export type MaterialsPlanBySap = ReadonlyMap<string, MaterialsPlanSapLine>;

export interface MaterialsPlanFetchParams {
  polygonId?: string;
  dateFrom?: string;
  dateTo?: string;
  activePolygonOnly?: boolean;
}

export function fetchMaterialsPlanBySap(
  objectService: ObjectService,
  params: MaterialsPlanFetchParams,
): Observable<{ hasPpd: boolean; planBySap: MaterialsPlanBySap }> {
  return objectService
    .getFinanceMaterialComparison(params.polygonId, {
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      activePolygonOnly: params.activePolygonOnly,
    })
    .pipe(
      map((result) => {
        if (!result?.hasPpd) {
          return { hasPpd: false, planBySap: new Map<string, MaterialsPlanSapLine>() };
        }
        const planBySap = new Map<string, MaterialsPlanSapLine>();
        for (const line of result.lines ?? []) {
          const sap = (line.sap ?? '').trim();
          if (!sap) continue;
          planBySap.set(sap, {
            plannedCost: Number(line.plannedCost) || 0,
            plannedQuantity: Number(line.plannedQuantity) || 0,
            unit: line.unit ?? null,
          });
        }
        return { hasPpd: true, planBySap };
      }),
      catchError(() => of({ hasPpd: false, planBySap: new Map<string, MaterialsPlanSapLine>() })),
    );
}

/** Finance API accepts a single polygon UUID; pass one when exactly one polygon is filtered. */
export function resolveMaterialsPlanPolygonId(
  polygonId: string | null | undefined,
  filterPolygonIds: readonly string[] | undefined,
): string | undefined {
  const direct = polygonId?.trim();
  if (direct) return direct;
  const ids = filterPolygonIds ?? [];
  if (ids.length === 1) return ids[0]?.trim() || undefined;
  return undefined;
}
